import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  AccountTree as JourneyIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Customer, CustomerTypeEnum } from "../../../store/services/customerService";
import { useGetSalesEmployeesQuery } from "../../../store/services/customerService";
import { useGetCompanyUrl } from "../../../hooks/useGetCompanyUrl";

interface CustomerHeroProps {
  customer: Customer;
}

const getInitials = (name: string) => {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const CustomerHero = ({ customer }: CustomerHeroProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const companyUrl = useGetCompanyUrl();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data: employees = [] } = useGetSalesEmployeesQuery();
  const sales = employees.find(
    (e) => e.ExternalUserNo === customer.SalesResponsible?.ExternalUserNo
  );

  const selectedJourney = customer.CustomerJourneyType?.find((j) => j.Selected);

  const statusInfo = (() => {
    if (customer.CustomerType === CustomerTypeEnum.Suspect) {
      return { label: "Suspekt", color: theme.palette.warning.main };
    }
    if (customer.CustomerType === CustomerTypeEnum.Prospect) {
      return { label: "Prospekt", color: theme.palette.info.main };
    }
    return {
      label: customer.CustomerId
        ? `Kund · ${customer.CustomerId}`
        : "Kund",
      color: theme.palette.success.main,
    };
  })();

  const primary = theme.palette.primary.main;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      {/* Breadcrumbs */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{
          mb: 1,
          color: "text.secondary",
          fontSize: 13,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="a"
          onClick={(e) => {
            e.preventDefault();
            navigate("/customers");
          }}
          sx={{
            cursor: "pointer",
            color: "inherit",
            textDecoration: "none",
            "&:hover": { color: "text.primary" },
          }}
        >
          Kunder
        </Box>
        <ChevronRightIcon sx={{ fontSize: 14, opacity: 0.5 }} />
        <Box
          component="a"
          onClick={(e) => {
            e.preventDefault();
            navigate(-1);
          }}
          sx={{
            cursor: "pointer",
            color: "inherit",
            textDecoration: "none",
            "&:hover": { color: "text.primary" },
          }}
        >
          Mina kunder
        </Box>
        <ChevronRightIcon sx={{ fontSize: 14, opacity: 0.5 }} />
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            fontWeight: 500,
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: { xs: 180, sm: "100%" },
          }}
        >
          {customer.Name}
        </Typography>
      </Stack>

      {/* Main row */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
          {isMobile && (
            <IconButton
              size="small"
              onClick={() => navigate(-1)}
              aria-label="back"
              sx={{ ml: -1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Box
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              borderRadius: "12px",
              bgcolor: alpha(primary, 0.08),
              border: `1px solid ${alpha(primary, 0.2)}`,
              color: primary,
              display: "grid",
              placeItems: "center",
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: "-0.01em",
            }}
          >
            {getInitials(customer.Name)}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography
                sx={{
                  fontSize: { xs: 18, sm: 22 },
                  fontWeight: 700,
                  letterSpacing: "-0.015em",
                  lineHeight: 1.15,
                  color: "text.primary",
                }}
              >
                {customer.Name}
              </Typography>
              <Chip
                size="small"
                label={statusInfo.label}
                sx={{
                  bgcolor: alpha(statusInfo.color, 0.12),
                  color: statusInfo.color,
                  fontWeight: 600,
                  fontSize: 12,
                  height: 22,
                  border: "none",
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              flexWrap="wrap"
              sx={{ mt: 0.5, color: "text.secondary", fontSize: 13 }}
            >
              {customer.LegalEntityValue && (
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <BusinessIcon sx={{ fontSize: 14 }} />
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    {customer.LegalEntityValue}
                  </Typography>
                </Stack>
              )}
              {customer.LegalEntityValue && customer.CustomerAddress && (
                <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled" }} />
              )}
              {customer.CustomerAddress && (
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <LocationIcon sx={{ fontSize: 14 }} />
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    {customer.CustomerAddress}
                  </Typography>
                </Stack>
              )}
              {selectedJourney && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled" }} />
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <JourneyIcon sx={{ fontSize: 14 }} />
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {selectedJourney.Name.trim()}
                    </Typography>
                  </Stack>
                </>
              )}
              {sales?.Name && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled" }} />
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <PersonIcon sx={{ fontSize: 14 }} />
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      Säljare {sales.Name}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              window.open(
                `${companyUrl}/customer/${customer.CustomerNo}`,
                "_blank"
              );
            }}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 1.75,
              py: 0.75,
              boxShadow: `0 1px 0 rgba(0,0,0,.04), 0 6px 16px -8px ${alpha(primary, 0.6)}`,
            }}
          >
            Öppna i KV
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CustomerHero;
